import React from "react";
import { Link } from "react-router-dom";
import {
  Target, Compass, ShieldCheck, HeartHandshake, Users, Award, GraduationCap,
  Factory, Zap, Wrench, Hammer, Droplets, Flame, Anchor, HandHelping, Cog,
  ArrowRight, Quote, Briefcase, Building2, MapPin, Calendar
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import EnquiryForm from "@/components/EnquiryForm";
import useSEO from "@/lib/useSEO";

const MD = {
  name: "Mr. Sanjay Singh Yadav",
  title: "Managing Director & Founder",
  education: "B.Tech, Electrical & Electronics Engineering",
  experience: "14+ years",
  domains: ["Thermal Power Plant", "Alumina Refinery", "Heavy Industrial Projects"],
  photo:
    "https://customer-assets-v7afamib.emergentagent.net/job_sarthi-jobs/artifacts/1jdws7ge_SANJAY%20pics.jpeg",
  quote:
    "India's frontline workforce is the backbone of every power plant, refinery and industrial site I have ever set foot in. Sarthi Associates exists to give these men and women the dignity, the platform and the opportunities they truly deserve.",
};

const COMPANY = {
  founded: "2026",
  hq: "Noida, India",
  workers: "1,500+",
  contractors: "20+",
  projects: "5+",
  states: "5+",
};

const EXECUTIVES = [
  {
    name: "Mr. Sudhanshu Pandey",
    title: "Executive Director",
    education: "B.Tech, Information Technology",
    experience: "14+ years",
    domain: "IT Sector",
    color: "orange",
    photo: "https://customer-assets-v7afamib.emergentagent.net/job_sarthi-jobs/artifacts/jq5f80pk_Sudhanshu%20.jpg.jpg",
  },
  {
    name: "Mr. Sandeep Kumar",
    title: "Executive Director",
    education: "B.Tech, Information Technology",
    experience: "14+ years",
    domain: "IT Sector",
    color: "blue",
    photo: "https://customer-assets-v7afamib.emergentagent.net/job_sarthi-jobs/artifacts/3a7v2ikx_Sanddep%20kumar.jpg",
  },
  {
    name: "Mr. Tanveer Siddiqui",
    title: "Executive Director",
    education: "B.Tech",
    experience: "20+ years",
    domain: "Power Sector",
    color: "amber",
    photo: "https://customer-assets-v7afamib.emergentagent.net/job_sarthi-jobs/artifacts/vhinqi90_Tanveer%20JPG.jpeg",
  },
];

const VALUES = [
  { icon: ShieldCheck, title: "Trust", body: "Verified workers, verified contractors. Every profile earns its place on our platform." },
  { icon: HeartHandshake, title: "Dignity", body: "Every frontline worker deserves respect, fair wages and a voice — regardless of literacy." },
  { icon: Award, title: "Skill First", body: "We match by real skill, not by paperwork. Your hands do the talking." },
  { icon: Users, title: "Community", body: "Workers, contractors and industry — grown together in one ecosystem." },
];

const SERVICES = [
  {
    icon: Flame, color: "orange",
    title: "Welders & Fitters",
    body: "Certified welders (SMAW/MIG/TIG) and precision fitters for power plants, refineries and structural work.",
  },
  {
    icon: Zap, color: "yellow",
    title: "Electricians & Technicians",
    body: "HT/LT electricians, instrumentation technicians and control-room operators for industrial installations.",
  },
  {
    icon: Droplets, color: "blue",
    title: "Plumbers & Pipe Fitters",
    body: "Industrial pipe fitters, plumbers and hydrotest specialists for utility and process piping.",
  },
  {
    icon: Anchor, color: "stone",
    title: "Riggers & Scaffolders",
    body: "Certified riggers, banksmen and scaffolders for shutdowns, erection and heavy lifts.",
  },
  {
    icon: Cog, color: "indigo",
    title: "Mechanical Technicians",
    body: "Turbine, pump, compressor and boiler technicians with real thermal-power exposure.",
  },
  {
    icon: HandHelping, color: "purple",
    title: "Helpers & Support Staff",
    body: "Skilled and semi-skilled helpers ready to be deployed across sites at short notice.",
  },
  {
    icon: Hammer, color: "amber",
    title: "Masons & Civil Work",
    body: "Foundation, brick and finishing masons for civil packages inside industrial facilities.",
  },
  {
    icon: Wrench, color: "green",
    title: "Contractor Registration",
    body: "End-to-end registration, compliance and manpower dispatch for EPC contractors and site owners.",
  },
];

const STATS = [
  { icon: Users, value: COMPANY.workers, label: "Registered Workers", color: "orange" },
  { icon: Building2, value: COMPANY.contractors, label: "Contractor Partners", color: "blue" },
  { icon: Briefcase, value: COMPANY.projects, label: "Projects Delivered", color: "green" },
  { icon: MapPin, value: COMPANY.states, label: "States Covered", color: "amber" },
];

export default function About() {
  const { t } = useI18n();

  useSEO({
    title: "About Sarthi Associates — India's Frontline Workforce Portal | Noida",
    description: "Founded 2026 in Noida. Led by MD Sanjay Singh Yadav (B.Tech Electrical, 14+ years in thermal power & alumina refineries) and Executive Directors Sudhanshu Pandey, Sandeep Kumar & Tanveer Siddiqui. 1,500+ verified workers, 20+ EPC partners, 5+ states.",
    path: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Sarthi Associates",
      "url": "https://sarthiassociates.com/about",
      "mainEntity": {
        "@type": "Organization",
        "name": "Sarthi Associates",
        "founder": { "@type": "Person", "name": "Sanjay Singh Yadav", "jobTitle": "Managing Director" },
        "foundingDate": "2026",
        "employee": [
          { "@type": "Person", "name": "Sudhanshu Pandey", "jobTitle": "Executive Director" },
          { "@type": "Person", "name": "Sandeep Kumar", "jobTitle": "Executive Director" },
          { "@type": "Person", "name": "Tanveer Siddiqui", "jobTitle": "Executive Director" },
        ],
      },
    },
  });

  return (
    <div>
      {/* Hero */}
      <section className="border-b-2 border-sarthi-border">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:py-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-sarthi-accent px-4 py-2 text-sm font-bold uppercase tracking-wider text-sarthi-primary" data-testid="about-tag">
            <ShieldCheck className="h-4 w-4" /> About Sarthi Associates
          </div>
          <h1 className="mt-4 font-display text-4xl font-black leading-[1.05] text-sarthi-text sm:text-5xl lg:text-6xl" data-testid="about-headline">
            Powering India's industry with{" "}
            <span className="text-sarthi-primary">skilled frontline hands.</span>
          </h1>
          <p className="mt-5 max-w-3xl text-lg font-medium text-sarthi-muted sm:text-xl">
            Sarthi Associates is a specialized manpower &amp; recruitment partner for
            thermal power plants, alumina refineries and heavy industrial projects across
            India. We connect verified frontline workers — welders, fitters, riggers,
            electricians, technicians, plumbers and helpers — with contractors and site
            owners who need dependable talent, fast.
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm font-bold text-sarthi-muted">
            <span className="inline-flex items-center gap-2"><Calendar className="h-4 w-4"/> Founded {COMPANY.founded}</span>
            <span className="hidden sm:inline">·</span>
            <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4"/> HQ {COMPANY.hq}</span>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b-2 border-sarthi-border bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-4 py-10 sm:px-6 md:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`} className={`sarthi-card p-5 cat-${s.color}`}>
              <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-white/70">
                <s.icon className="h-5 w-5" strokeWidth={2.5} />
              </div>
              <div className="font-display text-3xl font-black leading-none sm:text-4xl">{s.value}</div>
              <div className="mt-1 text-xs font-bold uppercase tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Managing Director */}
      <section className="border-b-2 border-sarthi-border">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-14 sm:px-6 lg:grid-cols-5 lg:gap-14">
          <div className="lg:col-span-2">
            <div className="sarthi-card overflow-hidden" data-testid="md-card">
              <div className="relative">
                <img
                  src={MD.photo}
                  alt={MD.name}
                  className="h-[420px] w-full object-cover object-top"
                  onError={(e) => { e.currentTarget.src = "https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?auto=compress&cs=tinysrgb&w=800"; }}
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <div className="font-display text-2xl font-extrabold text-white">{MD.name}</div>
                  <div className="text-sm font-bold uppercase tracking-wide text-sarthi-accent">{MD.title}</div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2">
                <div className="flex items-start gap-2">
                  <GraduationCap className="mt-0.5 h-5 w-5 flex-shrink-0 text-sarthi-primary" />
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-sarthi-muted">Education</div>
                    <div className="text-sm font-semibold text-sarthi-text">{MD.education}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Factory className="mt-0.5 h-5 w-5 flex-shrink-0 text-sarthi-primary" />
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-sarthi-muted">Experience</div>
                    <div className="text-sm font-semibold text-sarthi-text">{MD.experience}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-sm font-bold uppercase tracking-wider text-sarthi-primary">Leadership</div>
            <h2 className="mt-2 font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">
              From the desk of our Managing Director
            </h2>
            <div className="mt-5 flex items-start gap-3 rounded-2xl bg-sarthi-accent p-5">
              <Quote className="h-8 w-8 flex-shrink-0 text-sarthi-primary" />
              <p className="text-lg font-medium italic leading-relaxed text-sarthi-text">
                &ldquo;{MD.quote}&rdquo;
              </p>
            </div>

            <div className="mt-6 space-y-4 text-base leading-relaxed text-sarthi-muted">
              <p>
                <strong className="text-sarthi-text">{MD.name}</strong> holds a
                B.Tech in Electrical &amp; Electronics Engineering and brings over
                14 years of hands-on leadership across some of India&apos;s most
                demanding industrial environments — thermal power plants and
                alumina refineries.
              </p>
              <p>
                Having spent his career on-site alongside welders, riggers, electricians
                and helpers, he understands both the technical rigor of these
                facilities and the human reality of the workforce that keeps them
                running. Sarthi Associates is the outcome of that dual perspective —
                a platform built by an engineer, for the workers and contractors he
                grew up working with.
              </p>
              <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-3">
                {MD.domains.map((d) => (
                  <div key={d} className="rounded-xl border-2 border-sarthi-border bg-white px-3 py-2 text-center text-sm font-bold text-sarthi-secondary">
                    {d}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Executive Teams — regional (dynamic) */}
      <section className="border-b-2 border-sarthi-border bg-sarthi-accent/40">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <div className="mb-8">
            <div className="text-sm font-bold uppercase tracking-wider text-sarthi-primary">Our Executive Teams</div>
            <h2 className="mt-1 font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">
              Regional leaders. On the ground with you.
            </h2>
            <p className="mt-2 max-w-2xl text-base font-medium text-sarthi-muted">
              We organise our leadership across five zones — North, South, East, West and Central — so every client gets a direct line to a leader in their region.
            </p>
          </div>
          <a href="/executive-teams" data-testid="executive-teams-link" className="sarthi-btn-primary inline-flex items-center gap-2 px-6 py-3 text-white">
            Meet your regional team →
          </a>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-14 sm:px-6 md:grid-cols-2">
          <div className="sarthi-card p-8" data-testid="vision-card">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-sarthi-primary text-white shadow-[0_4px_0_0_#B84A25]">
              <Compass className="h-7 w-7" strokeWidth={2.5} />
            </div>
            <h3 className="font-display text-2xl font-extrabold text-sarthi-text sm:text-3xl">Our Vision</h3>
            <p className="mt-3 text-lg font-medium leading-relaxed text-sarthi-muted">
              To become India&apos;s most trusted digital bridge between frontline industrial
              workers and the projects that need them — where a welder in a village and
              a project manager in Mumbai are only one tap apart.
            </p>
          </div>
          <div className="sarthi-card p-8" data-testid="mission-card">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-sarthi-secondary text-white shadow-[0_4px_0_0_#1F3A64]">
              <Target className="h-7 w-7" strokeWidth={2.5} />
            </div>
            <h3 className="font-display text-2xl font-extrabold text-sarthi-text sm:text-3xl">Our Mission</h3>
            <ul className="mt-3 space-y-3 text-base font-medium leading-relaxed text-sarthi-muted">
              <li className="flex gap-3"><span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-sarthi-primary" /> Empower frontline workers with voice-first, multilingual access to real jobs.</li>
              <li className="flex gap-3"><span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-sarthi-primary" /> Give contractors verified, skill-matched manpower in hours, not weeks.</li>
              <li className="flex gap-3"><span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-sarthi-primary" /> Uphold fair wages, safety compliance and dignity across every deployment.</li>
              <li className="flex gap-3"><span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-sarthi-primary" /> Build the largest verified skill database of India&apos;s industrial workforce.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-y-2 border-sarthi-border">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <div className="mb-8">
            <div className="text-sm font-bold uppercase tracking-wider text-sarthi-primary">What we stand for</div>
            <h2 className="mt-1 font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => (
              <div key={v.title} data-testid={`value-${v.title.toLowerCase()}`} className="sarthi-card p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-sarthi-accent text-sarthi-primary">
                  <v.icon className="h-6 w-6" strokeWidth={2.5} />
                </div>
                <div className="font-display text-xl font-extrabold text-sarthi-text">{v.title}</div>
                <p className="mt-2 text-base font-medium text-sarthi-muted">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services / Professionals */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-sm font-bold uppercase tracking-wider text-sarthi-primary">Manpower categories</div>
              <h2 className="mt-1 font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">Professionals we deploy</h2>
              <p className="mt-2 text-base font-medium text-sarthi-muted">Every worker is skill-verified, safety-briefed and site-ready.</p>
            </div>
            <Link to="/jobs" data-testid="about-browse-jobs" className="sarthi-btn-outline px-4 py-3">
              Browse open jobs →
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <div key={s.title} data-testid={`service-${s.title.split(" ")[0].toLowerCase()}`} className="sarthi-card p-6">
                <div className={`mb-4 flex h-14 w-14 items-center justify-center rounded-2xl cat-${s.color}`}>
                  <s.icon className="h-7 w-7" strokeWidth={2.5} />
                </div>
                <h3 className="font-display text-xl font-extrabold text-sarthi-text">{s.title}</h3>
                <p className="mt-2 text-base font-medium text-sarthi-muted">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t-2 border-sarthi-border bg-sarthi-accent/70">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 px-4 py-14 text-center sm:px-6">
          <h2 className="font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">
            Ready to build with Sarthi?
          </h2>
          <p className="max-w-2xl text-lg font-medium text-sarthi-muted">
            Whether you&apos;re a skilled frontline worker looking for your next site, or a
            contractor scaling up your next shutdown — we&apos;re one tap away.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link to="/register?role=worker" data-testid="about-cta-worker" className="sarthi-btn-primary inline-flex items-center justify-center gap-2 px-6 py-4 text-lg text-white">
              I want work <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to="/register?role=contractor" data-testid="about-cta-contractor" className="sarthi-btn-secondary inline-flex items-center justify-center gap-2 px-6 py-4 text-lg text-white">
              I am a contractor <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact / Enquiry */}
      <section id="contact" className="border-t-2 border-sarthi-border bg-white scroll-mt-24">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-5 lg:gap-14">
            <div className="lg:col-span-2">
              <div className="text-sm font-bold uppercase tracking-wider text-sarthi-primary">Talk to us</div>
              <h2 className="mt-2 font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">
                Request skilled manpower for your project
              </h2>
              <p className="mt-3 text-base font-medium leading-relaxed text-sarthi-muted">
                EPC contractors, plant owners and industrial project managers — tell us
                your requirement and our team will deploy a verified, safety-compliant
                crew for your site.
              </p>
              <div className="mt-6 space-y-4">
                <ContactRow
                  label="Direct line"
                  value="+91 91318 47732"
                  href="tel:+919131847732"
                  testid="contact-phone"
                />
                <ContactRow
                  label="Support &amp; operations"
                  value="contact@sarthiassociates.com"
                  href="mailto:contact@sarthiassociates.com"
                  testid="contact-email"
                />
                <ContactRow
                  label="General enquiries"
                  value="info@sarthiassociates.com"
                  href="mailto:info@sarthiassociates.com"
                  testid="contact-info-email"
                />
                <ContactRow
                  label="Managing Director"
                  value="cmd@sarthiassociates.com"
                  href="mailto:cmd@sarthiassociates.com"
                  testid="contact-md-email"
                />
                <ContactRow
                  label="Headquarters"
                  value={`${COMPANY.hq} · Serving pan-India`}
                  testid="contact-hq"
                />
              </div>
              <div className="mt-6 rounded-2xl bg-sarthi-accent/70 p-5">
                <div className="text-sm font-bold uppercase tracking-wide text-sarthi-primary">Response SLA</div>
                <div className="mt-1 font-display text-2xl font-extrabold text-sarthi-text">Within 24 hours</div>
                <div className="text-sm font-medium text-sarthi-muted">Working days, from a real human, not a bot.</div>
              </div>
            </div>
            <div className="lg:col-span-3">
              <EnquiryForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactRow({ label, value, href, testid }) {
  const inner = (
    <div className="sarthi-card p-4">
      <div className="text-xs font-bold uppercase tracking-wide text-sarthi-muted">{label}</div>
      <div className="mt-0.5 font-display text-lg font-extrabold text-sarthi-text">{value}</div>
    </div>
  );
  return href ? (
    <a data-testid={testid} href={href} className="block transition-transform hover:-translate-y-0.5">{inner}</a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
}
